import '../../rules-table.css';
import { useAppDispatch } from 'hooks';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as printerActions from '../../actions/printerActions';
import * as readerActions from '../../actions/readerActions';
import * as nodesActions from '../../actions/nodesActions';
import * as autoPrintRulesActions from '../../actions/autoPrintRulesActions';
import { getAutoPrintRules, getNodes, getPrinterNames, getReaderNames } from '../../reducers/selectors';
import AutoPrintRuleModal from './AutoPrintRuleModal';
import { AutoPrintRule, PrintMode } from 'reducers/autoPrintRulesReducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faTriangleExclamation, faWrench } from '@fortawesome/free-solid-svg-icons';
import { Node } from 'reducers/nodesReducer';
import * as http from '../../http';

const AutoPrintRulesPage = () => {
  const dispatch = useAppDispatch();
  const rules: AutoPrintRule[] = useSelector(getAutoPrintRules);
  const readerNames: string[] = useSelector(getReaderNames);
  const burnerNames: string[] = useSelector(getPrinterNames);
  const nodes: Node[] = useSelector(getNodes);
  const [editingRule, setEditingRule] = useState<AutoPrintRule | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [deletingRule, setDeletingRule] = useState<AutoPrintRule | undefined>(undefined);
  const toaster = {
    success: message => dispatch({ type: 'SUCCESS', message }),
    info: message => dispatch({ type: 'INFO', message }),
    warning: message => dispatch({ type: 'WARNING', message }),
    error: message => dispatch({ type: 'ERROR', message }),
  };

  const handleCloseModal = () => {
    setEditingRule(undefined);
    setShowModal(false);
  };
  const handleShowModal = (rule: AutoPrintRule | undefined) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const warnTooltip = (rule: AutoPrintRule) => {
    return (
      <Tooltip id="tooltip">
        <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{'Node ' + rule.aeTitle + ' is disabled'}</p>
      </Tooltip>
    );
  };

  const errorTooltip = (rule: AutoPrintRule) => {
    return (
      <Tooltip id="tooltip">
        <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{getInvalidValueHint(rule)}</p>
      </Tooltip>
    );
  };

  useEffect(() => {
    dispatch(printerActions.getPrinters());
    dispatch(readerActions.getReaders());
    dispatch(nodesActions.getNodes());
    dispatch(autoPrintRulesActions.getAutoPrintRules());
  }, [dispatch]);

  function hasInvalidValue(rule: AutoPrintRule) {
    return (
      nodes.find(node => node.aeTitle === rule.aeTitle) === undefined ||
      (rule.burnerName !== undefined && !burnerNames.includes(rule.burnerName)) ||
      (rule.readerName !== undefined && !readerNames.includes(rule.readerName))
    );
  }

  function getInvalidValueHint(rule: AutoPrintRule): string {
    let message = 'The rule is invalid due to errors:';
    let concatWarningText = '';
    const originNode = nodes.find(node => node.aeTitle === rule.aeTitle);
    if (originNode === undefined) {
      concatWarningText = concatWarningText.concat('\n- The configured origin node does not exist');
    }
    if (rule.burnerName !== undefined && !burnerNames.includes(rule.burnerName)) {
      concatWarningText = concatWarningText.concat('\n- The configured printer does not exist');
    }
    if (rule.readerName !== undefined && !readerNames.includes(rule.readerName)) {
      concatWarningText = concatWarningText.concat('\n- The configured reader does not exist');
    }
    return message.concat(concatWarningText);
  }

  function isNodeDisabled(aeTitle: string | undefined) {
    const foundNode = nodes.find(node => node.aeTitle === aeTitle);
    if (foundNode) {
      if (!foundNode.enabled) {
        return true;
      }
    }
    return false;
  }

  return (
    <Card className="page-panel-common">
      <Card.Body>
        <h3>Auto print rules</h3>
        <Col>
          <Row style={{ placeContent: 'end' }}>
            <Button onClick={() => handleShowModal(undefined)} className="align-self-end" size="sm" variant="primary">
              Add rule
            </Button>
          </Row>
          <Row>
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Printer</th>
                  <th>Reader</th>
                  <th>Mode</th>
                  <th>Timeout (in minutes)</th>
                  <th>Include Pdi</th>
                  <th>Enabled</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(rule => {
                  return (
                    <tr key={rule.id}>
                      <td>{rule.aeTitle}</td>
                      <td>{rule.burnerName}</td>
                      <td>{rule.readerName}</td>
                      <td>{PrintMode[rule.mode]}</td>
                      <td>{rule.timeout}</td>
                      <td>
                        {rule.includePdi ? <Badge variant="success">True</Badge> : <Badge variant="danger">False</Badge>}
                      </td>
                      <td>
                        {rule.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="danger">Disabled</Badge>}
                      </td>
                      <td>
                        <FontAwesomeIcon className="clickable-icon" icon={faWrench} onClick={() => handleShowModal(rule)} />
                        <FontAwesomeIcon
                          className="clickable-icon printer-table-actions"
                          icon={faTrash}
                          onClick={() => setDeletingRule(rule)}
                        />
                        {isNodeDisabled(rule.aeTitle) ? (
                          <OverlayTrigger placement="bottom" overlay={warnTooltip(rule)}>
                            <FontAwesomeIcon icon={faTriangleExclamation} className="warn-info-icon" />
                          </OverlayTrigger>
                        ) : (
                          <React.Fragment />
                        )}
                        {hasInvalidValue(rule) ? (
                          <OverlayTrigger placement="bottom" overlay={errorTooltip(rule)}>
                            <FontAwesomeIcon icon={faTriangleExclamation} className="error-info-icon" />
                          </OverlayTrigger>
                        ) : (
                          <React.Fragment />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Row>
        </Col>
      </Card.Body>
      {deletingRule && (
        <DeleteAutoPrintRulePopup
          rule={deletingRule!}
          onClose={() => setDeletingRule(undefined)}
          onConfirmDelete={() => {
            http
              .del(`/rest/react/auto-print-rules/${deletingRule.id}`)
              .then(res => {
                dispatch(autoPrintRulesActions.getAutoPrintRules());
                toaster.success(`Successfully deleted rule`);
                setDeletingRule(undefined);
              })
              .catch(e => toaster.error(e.message));
          }}
        />
      )}
      {showModal && (
        <AutoPrintRuleModal
          editMode={editingRule !== undefined}
          rule={editingRule}
          show={showModal}
          handleClose={handleCloseModal}
        />
      )}
    </Card>
  );
};

const DeleteAutoPrintRulePopup = ({ rule, onClose, onConfirmDelete }) => (
  <Modal show>
    <Modal.Header>
      <Modal.Title>Deleting auto print rule</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Deleting auto print rule configured with node <i>{rule.aeTitle}</i>, printer <i>{rule.burnerName}</i> and mode{' '}
        <i>{rule.mode}</i>
      </p>
      <p>Are you sure?</p>
    </Modal.Body>
    <Modal.Footer>
      <div style={{ textAlign: 'right' }}>
        <Button onClick={onClose} variant="outline-secondary" style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirmDelete}>
          Confirm delete
        </Button>
      </div>
    </Modal.Footer>
  </Modal>
);

export default AutoPrintRulesPage;
